/**
 * Monoline UI — Telegram HTML qatlami.
 *
 * Bazadagi shablon tanasi va bot yuboradigan javob — bu oddiy matn emas,
 * `parse_mode=HTML` bilan ketadigan TELEGRAM HTML: `<b>`, `<a href>`,
 * `<tg-emoji>`, `\n`. Panelda uni ko'rsatishning uch yo'li bor edi va
 * uchalasi ham yaroqsiz:
 *
 *   1. `dangerouslySetInnerHTML` — MUMKIN EMAS. Shablon tanasiga ishonib
 *      bo'lmaydi: AI qoralamasi fuqaroning Telegramda yozgan matnidan
 *      quriladi (`service.py` → `maybe_save_draft_template`), ya'ni satrni
 *      botga xabar yozgan HAR QANDAY odam boshqaradi. `innerHTML` bo'lsa
 *      `<img src=x onerror=…>` yoki `<a href="javascript:…">` admin
 *      sessiyasida bajariladi.
 *   2. Regex-sanitizator + innerHTML — bunday himoya tarixda har doim
 *      aylanib o'tilgan; to'g'ri qilish uchun DOMPurify kerak, ya'ni yangi
 *      bog'liqlik.
 *   3. Teglarni butunlay tozalash — xavfsiz, lekin ma'noni yo'qotadi:
 *      qalin sarlavha va bandlar bir qorishiq satrga aylanadi, admin
 *      xatosi (`<br>`, yopilmagan teg) esa YASHIRINADI.
 *
 * Shuning uchun bu yerda TO'RTINCHI yo'l: satr allowlist-parser bilan
 * tokenlarga bo'linadi va har token REACT TUGUNIGA aylanadi. `innerHTML`
 * hech qachon chaqirilmaydi — demak `onerror`/`onload`/`javascript:` uchun
 * kirish nuqtasi konstruksiya darajasida yo'q. Xavfsizlik "qora ro'yxat
 * to'liqmi" degan savolga emas, tuzilishga tayanadi.
 *
 * Noma'lum teg O'CHIRILMAYDI — matn sifatida ko'rsatiladi (`<br>` bo'lib
 * ko'rinadi) va `validateTgHtml` uni xato qilib qaytaradi. Bu ataylab:
 * `<br>` bilan shablon Telegramga UMUMAN ketmaydi (400 can't parse
 * entities), demak uni ko'rsatish — funksiya, nuqson emas.
 *
 * Tashqi bog'liqlik: faqat `react` (+ kit ichidagi `Link`, `PremiumEmoji`).
 */

import { forwardRef, useMemo } from 'react'
import { cx, Link } from './primitives.jsx'
import { PremiumEmoji } from './emoji.jsx'

/* ─────────────────────────────────────────────────────────────
   Allowlist — Telegram Bot API qabul qiladigan teglarning to'liq ro'yxati
   ───────────────────────────────────────────────────────────── */

/**
 * Teg nomi → ichki tur. Bir necha nom bir turga tushadi (`b`/`strong`),
 * chunki Telegram uchun ular sinonim.
 * Bu jadvalda YO'Q hamma narsa — xato: `br`, `p`, `div`, `ul`, `li`, `h1`,
 * `img`, `script`, `iframe`, `font`, `table`…
 */
const TG_TAGS = {
  b: 'b', strong: 'b',
  i: 'i', em: 'i',
  u: 'u', ins: 'u',
  s: 's', strike: 's', del: 's',
  code: 'code',
  pre: 'pre',
  blockquote: 'quote',
  a: 'a',
  'tg-emoji': 'emoji',
  'tg-spoiler': 'spoiler',
  span: 'spoiler',            // faqat class="tg-spoiler" bilan
}

/** O'qiladigan nom — xato matnida ishlatiladi. */
const TAG_HINT = {
  br: 'satr ko\'chirish uchun oddiy \\n yozing',
  p: 'band ajratish uchun ikkita \\n yozing',
  div: 'band ajratish uchun ikkita \\n yozing',
  ul: 'ro\'yxat uchun "— " belgisi bilan qator yozing',
  ol: 'ro\'yxat uchun "1. " belgisi bilan qator yozing',
  li: 'ro\'yxat uchun "— " belgisi bilan qator yozing',
  img: 'rasm xabar sifatida alohida yuboriladi',
  h1: 'sarlavha uchun <b> ishlating',
  h2: 'sarlavha uchun <b> ishlating',
  h3: 'sarlavha uchun <b> ishlating',
}

// Havolada ruxsat etilgan sxemalar. `javascript:`, `data:`, `vbscript:` —
// ro'yxatda yo'q, ya'ni bunday havola teg emas, oddiy matn bo'lib qoladi.
const SAFE_HREF = /^(https?:\/\/|tg:\/\/|mailto:)/i

// Teg va atributlarni ajratish. Tirnoq ichidagi `>` tegni yopmasligi uchun
// atribut qismi ataylab tirnoqlarni hisobga oladi.
const TAG_SRC = '<(/?)([a-zA-Z][a-zA-Z0-9-]*)((?:"[^"]*"|\'[^\']*\'|[^>"\'])*)>'
const ATTR_SRC = '([a-zA-Z:-]+)\\s*=\\s*(?:"([^"]*)"|\'([^\']*)\'|([^\\s"\'>]+))'

// {ism}, {ticket_raqami} — shablon o'zgaruvchilari
const VAR_SRC = '\\{([a-zA-Z_][a-zA-Z0-9_]*)\\}'

function readAttrs(src) {
  const out = {}
  for (const m of String(src || '').matchAll(new RegExp(ATTR_SRC, 'g'))) {
    out[m[1].toLowerCase()] = m[2] ?? m[3] ?? m[4] ?? ''
  }
  return out
}

/** Stekda shu turdagi eng yaqin ochiq tegni topadi (topilmasa −1). */
function findOpen(stack, kind) {
  for (let i = stack.length - 1; i > 0; i--) {
    if (stack[i].kind === kind) return i
  }
  return -1
}

/**
 * Telegram HTML ni daraxtga aylantiradi.
 *
 * @returns {{root:object, errors:Array, vars:string[]}}
 *   root.children — `{t:'text', v}` yoki `{t:'tag', kind, name, attrs, children}`
 */
export function parseTgHtml(value) {
  const src = String(value ?? '')
  const root = { kind: 'root', children: [] }
  const stack = [root]
  const errors = []

  const top = () => stack[stack.length - 1]
  const pushText = (v) => {
    if (!v) return
    const list = top().children
    const prev = list[list.length - 1]
    // Qo'shni matn bo'laklari birlashtiriladi — daraxt sayoz qoladi
    if (prev && prev.t === 'text') prev.v += v
    else list.push({ t: 'text', v })
  }

  const re = new RegExp(TAG_SRC, 'g')
  let last = 0
  let m

  while ((m = re.exec(src)) !== null) {
    pushText(src.slice(last, m.index))
    last = m.index + m[0].length

    const closing = m[1] === '/'
    const name = m[2].toLowerCase()
    const kind = TG_TAGS[name]

    // ── Telegram bilmaydigan teg ── matn bo'lib qoladi, xato yoziladi
    if (!kind) {
      errors.push({
        code: 'unknown_tag', tag: name, index: m.index,
        message: `<${name}> — Telegram bu tegni qabul qilmaydi` +
          (TAG_HINT[name] ? `, ${TAG_HINT[name]}` : ''),
      })
      pushText(m[0])
      continue
    }

    // ── Yopiluvchi teg ──
    if (closing) {
      const at = findOpen(stack, kind)
      if (at < 0) {
        errors.push({
          code: 'stray_close', tag: name, index: m.index,
          message: `</${name}> — ochilmagan teg yopilyapti`,
        })
        pushText(m[0])
        continue
      }
      // Ustidagilar yopilmay qolgan: <b><i>matn</b>
      for (let i = stack.length - 1; i > at; i--) {
        errors.push({
          code: 'unclosed', tag: stack[i].name, index: stack[i].index,
          message: `<${stack[i].name}> yopilmagan — teglar bir-birining ichida to'g'ri yopilishi kerak`,
        })
      }
      stack.length = at
      continue
    }

    // ── Ochiluvchi teg ── atributlar tekshiriladi
    const attrs = readAttrs(m[3])

    if (name === 'span' && attrs.class !== 'tg-spoiler') {
      errors.push({
        code: 'bad_span', tag: name, index: m.index,
        message: '<span> faqat class="tg-spoiler" bilan ishlaydi',
      })
      pushText(m[0])
      continue
    }
    if (kind === 'a' && !SAFE_HREF.test(attrs.href || '')) {
      errors.push({
        code: 'bad_href', tag: name, index: m.index,
        message: attrs.href
          ? `havola sxemasi qabul qilinmaydi: ${attrs.href.slice(0, 40)}`
          : '<a> tegida href yo\'q',
      })
      pushText(m[0])
      continue
    }
    if (kind === 'emoji' && !/^\d+$/.test(attrs['emoji-id'] || '')) {
      errors.push({
        code: 'bad_emoji_id', tag: name, index: m.index,
        message: '<tg-emoji> da emoji-id raqam bo\'lishi kerak',
      })
      pushText(m[0])
      continue
    }

    const node = { t: 'tag', kind, name, attrs, index: m.index, children: [] }
    top().children.push(node)
    stack.push(node)
  }

  pushText(src.slice(last))

  // Oxirigacha yopilmay qolganlar
  for (let i = stack.length - 1; i > 0; i--) {
    errors.push({
      code: 'unclosed', tag: stack[i].name, index: stack[i].index,
      message: `<${stack[i].name}> yopilmagan — Telegram xabarni rad etadi`,
    })
  }

  const vars = []
  for (const v of src.matchAll(new RegExp(VAR_SRC, 'g'))) {
    if (!vars.includes(v[1])) vars.push(v[1])
  }

  return { root, errors, vars }
}

/**
 * Saqlashdan oldingi tekshiruv.
 *
 * Bu bezak emas: xato teg → Telegram 400 `can't parse entities` → xabar
 * fuqaroga UMUMAN bormaydi. Shuning uchun forma xatoni saqlashdan OLDIN
 * ko'rsatishi kerak.
 *
 * @param   {string} text  Telegram HTML
 * @returns {{ok:boolean, errors:Array<{code,tag,index,count,message}>, vars:string[]}}
 *   Bir xil `code+tag` juftligi bir marta qaytadi, `count` — nechta joyda
 *   uchragani (43 ta `<br>` uchun 43 qator ogohlantirish shovqin bo'lardi).
 */
export function validateTgHtml(text) {
  const { errors, vars } = parseTgHtml(text)
  const seen = new Map()
  for (const e of errors) {
    const key = `${e.code}|${e.tag}`
    const prev = seen.get(key)
    if (prev) prev.count += 1
    else seen.set(key, { ...e, count: 1 })
  }
  const list = [...seen.values()]
  return { ok: list.length === 0, errors: list, vars }
}

/**
 * Teglarsiz sof matn: qidiruv, solishtirish va oddiy `textarea` uchun.
 * `<tg-emoji>` ichidagi fallback belgi qoladi, ya'ni matn fuqaro
 * ko'radigan narsaga yaqin bo'ladi.
 */
export function stripTgHtml(text) {
  const { root } = parseTgHtml(text)
  let out = ''
  const walk = (nodes) => {
    for (const n of nodes) {
      if (n.t === 'text') out += n.v
      else walk(n.children)
    }
  }
  walk(root.children)
  return out
}

/* ─────────────────────────────────────────────────────────────
   TgText — daraxtni React tugunlariga chizish
   ───────────────────────────────────────────────────────────── */

function renderNodes(nodes, ctx, path = 't') {
  return nodes.map((n, i) => {
    const key = `${path}-${i}`
    if (n.t === 'text') return n.v

    const kids = () => renderNodes(n.children, ctx, key)

    switch (n.kind) {
      case 'b': return <strong key={key}>{kids()}</strong>
      case 'i': return <em key={key}>{kids()}</em>
      case 'u': return <u key={key}>{kids()}</u>
      case 's': return <s key={key}>{kids()}</s>
      case 'code': return <code key={key} className="ui-code">{kids()}</code>
      case 'pre': return <pre key={key} className="ui-tg__pre">{kids()}</pre>
      case 'quote': return <blockquote key={key} className="ui-tg__quote">{kids()}</blockquote>

      case 'spoiler':
        // Yashirin matn: fokus bilan ham ochiladi (klaviatura), ochilgunicha
        // ekran o'quvchi uchun nomlangan.
        return (
          <span
            key={key}
            className="ui-tg__spoiler"
            tabIndex={0}
            role="button"
            aria-label="Yashirilgan matn — ochish"
          >
            {kids()}
          </span>
        )

      case 'emoji': {
        const em = ctx.emojiById.get(String(n.attrs['emoji-id']))
        // Kutubxonada yo'q bo'lsa ichidagi fallback belgi qoladi —
        // fuqaro premium emoji ko'rmaganda aynan shuni ko'radi.
        if (!em) return <span key={key}>{kids()}</span>
        return <PremiumEmoji key={key} emoji={em} size={ctx.emojiSize} />
      }

      case 'a': {
        const href = n.attrs.href
        // Kartochka preview'ida havola BOSILMAYDI: kartaning o'zi bosiladigan
        // yuza, ichidagi havola esa bosishni o'g'irlaydi.
        if (ctx.links !== 'render') {
          return <span key={key} className="ui-tg__link" title={href}>{kids()}</span>
        }
        return (
          <Link key={key} href={href} target="_blank">{kids()}</Link>
        )
      }

      default: return <span key={key}>{kids()}</span>
    }
  })
}

/**
 * Telegram HTML ni xavfsiz ko'rsatadi.
 *
 * Kesish BELGI bo'yicha emas, `clamp` bilan VIZUAL bajariladi: `substring(120)`
 * tegning o'rtasidan kesib buzuq daraxt beradi va har kartada ko'rinadigan
 * matn uzunligi har xil bo'ladi (`<b>` juftligi 7 belgi yeydi). `clamp`
 * esa satr sanaydi — kartalar bir xil balandlikda turadi.
 *
 * @param value      Telegram HTML satri (`Template.body_uz` va shu kabi)
 * @param emojis     premium emoji kutubxonasi — `<tg-emoji>` jonli chiziladi
 * @param clamp      ko'rinadigan satrlar soni; 0 — cheklovsiz
 * @param links      'render' — bosiladigan havola | 'text' — oddiy matn
 * @param variant    'block' — `\n` saqlanadi | 'inline' — bir qatorga
 * @param emojiSize  premium emoji o'lchami (px)
 * @param as         tashqi teg (standart `div`, inline uchun `span`)
 */
export const TgText = forwardRef(function TgText(
  {
    value,
    emojis = [],
    clamp = 0,
    links = 'render',
    variant = 'block',
    emojiSize = 18,
    as: Tag = null,
    emptyText = '',
    className,
    style,
    ...rest
  },
  ref
) {
  const emojiById = useMemo(
    () => new Map((emojis || []).map((e) => [String(e.custom_emoji_id), e])),
    [emojis]
  )
  const { root } = useMemo(() => parseTgHtml(value), [value])

  const El = Tag || (variant === 'inline' ? 'span' : 'div')
  const empty = !String(value ?? '').trim()

  return (
    <El
      ref={ref}
      className={cx(
        'ui-tg',
        variant === 'inline' && 'ui-tg--inline',
        clamp > 0 && 'ui-clamp',
        empty && 'ui-muted',
        className
      )}
      style={clamp > 0 ? { '--ui-clamp-lines': clamp, ...style } : style}
      {...rest}
    >
      {empty ? emptyText : renderNodes(root.children, { emojiById, links, emojiSize })}
    </El>
  )
})
TgText.displayName = 'TgText'
